


import React, { useEffect, useMemo, useState } from 'react';
import { compareSavedSynastries } from '../services/synastryApi';
import { useLocation, useNavigate } from 'react-router-dom';
import { clearLegacySavedSynastryStorage } from '../utils/synastryPrivateStorage';
import { localizedValue } from '../localization/presentationLanguage';

const synText = (es, en) => localizedValue({ es, en });

// --- Persisted comparison candidate helpers ---
const DISMISSED_COMPARISON_CANDIDATES_KEY = 'zodiacus:comparisonDismissedSavedIds';

function safeParseJsonArray(value) {
  if (!value || typeof value !== 'string') return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function readDismissedComparisonCandidateIds() {
  if (typeof window === 'undefined' || !window.localStorage) return [];
  return safeParseJsonArray(window.localStorage.getItem(DISMISSED_COMPARISON_CANDIDATES_KEY))
    .map((value) => String(value || '').trim())
    .filter(Boolean);
}

function writeDismissedComparisonCandidateIds(ids) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  const normalized = Array.from(new Set((Array.isArray(ids) ? ids : []).map((value) => String(value || '').trim()).filter(Boolean)));
  window.localStorage.setItem(DISMISSED_COMPARISON_CANDIDATES_KEY, JSON.stringify(normalized));
}

function purgeSavedCandidateFromComparisonStorage(candidateId) {
  const safeId = String(candidateId || '').trim();
  if (!safeId || typeof window === 'undefined' || !window.localStorage) return;

  clearLegacySavedSynastryStorage(window.localStorage);
}


const containerStyle = {
  minHeight: '100svh',
  width: '100%',
  background:
    'linear-gradient(180deg, #22043A 0%, #2E0A4F 20%, #3B0F63 46%, #4B0082 72%, #6E34A9 88%, #DAA520 100%)',
  backgroundColor: '#22043A',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
  boxSizing: 'border-box',
  overflowX: 'hidden',
  paddingLeft: '18px',
  paddingRight: '18px',
  paddingTop: 'calc(26px + env(safe-area-inset-top, 0px))',
  paddingBottom: 'calc(26px + env(safe-area-inset-bottom, 0px))',
  color: '#fff',
};

const cardStyle = {
  width: '100%',
  maxWidth: 920,
  borderRadius: 22,
  padding: '22px 22px 26px',
  marginTop: 6,
  background:
    'linear-gradient(180deg, rgba(20,0,44,0.96) 0%, rgba(16,0,36,0.94) 100%)',
  backgroundColor: 'rgba(20,0,44,0.96)',
  border: '1px solid rgba(218,165,32,0.42)',
  boxShadow:
    '0 14px 34px rgba(0,0,0,0.42), inset 0 1px 0 rgba(255,255,255,0.04)',
};

const panelStyle = {
  background:
    'linear-gradient(180deg, rgba(255,255,255,0.035), rgba(255,255,255,0.015)), rgba(0,0,0,0.22)',
  border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 16,
  padding: 14,
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04), 0 8px 20px rgba(0,0,0,0.22)',
};

const headingKickerStyle = {
  fontSize: 11,
  fontWeight: 900,
  letterSpacing: 0.9,
  textTransform: 'uppercase',
  color: '#F7D26A',
  opacity: 0.95,
  marginBottom: 6,
};

const panelTitleStyle = {
  fontSize: 15,
  lineHeight: 1.2,
  fontWeight: 900,
  color: '#FFF4CC',
};

const mutedStyle = {
  fontSize: 12,
  color: 'rgba(255,255,255,0.78)',
  lineHeight: 1.45,
};

const buttonBaseStyle = {
  borderRadius: 999,
  padding: '10px 16px',
  border: 'none',
  cursor: 'pointer',
  fontWeight: 900,
  fontSize: 13,
  transition: 'transform 120ms ease, opacity 120ms ease',
};

const primaryButtonStyle = {
  ...buttonBaseStyle,
  background: 'linear-gradient(135deg,#DAA520,#FFD700)',
  color: '#4B0082',
};

const secondaryButtonStyle = {
  ...buttonBaseStyle,
  background: 'rgba(255,255,255,0.06)',
  color: '#fff',
  border: '1px solid rgba(255,255,255,0.14)',
};

const disabledButtonStyle = {
  opacity: 0.55,
  cursor: 'not-allowed',
};

const statCardStyle = {
  background: 'linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.025))',
  border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 14,
  padding: '12px 12px 13px',
};

const microLabelStyle = {
  fontSize: 11,
  fontWeight: 900,
  color: '#F7D26A',
  letterSpacing: 0.35,
  textTransform: 'uppercase',
};

const insightTextStyle = {
  fontSize: 13,
  lineHeight: 1.5,
  color: 'rgba(255,255,255,0.88)',
};

const bulletCardStyle = {
  display: 'flex',
  gap: 8,
  alignItems: 'flex-start',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 12,
  padding: '10px 12px',
};

const radarShellStyle = {
  background:
    'linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02)), rgba(0,0,0,0.16)',
  border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 18,
  padding: 14,
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04), 0 10px 24px rgba(0,0,0,0.20)',
};

const radarLegendDotStyle = (background) => ({
  width: 10,
  height: 10,
  borderRadius: 999,
  background,
  boxShadow: `0 0 10px ${background}`,
  flex: '0 0 auto',
  marginTop: 3,
});

function n(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function formatSignedDelta(value) {
  const num = n(value);
  if (num > 0) return `+${num}`;
  return `${num}`;
}

function getDeltaTone(value) {
  const num = n(value);
  if (num > 0) return '#9DFFB0';
  if (num < 0) return '#FFAEAE';
  return '#F4E7BF';
}

function getWinnerLabel(flag, fallbackA, fallbackB) {
  if (flag === 'readingA') return fallbackA;
  if (flag === 'readingB') return fallbackB;
  return 'Empate';
}

// 1) Reemplazá COMPLETA esta función
function normalizeDisplayTitle(title, fallback = 'Lectura') {
  const raw = String(title || '').trim();
  if (!raw) return fallback;
  if (isPlaceholderPairTitle(raw)) return fallback;

  const normalized = raw
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

  if (
    normalized === 'persona a ✦ persona b' ||
    normalized === 'persona a y persona b' ||
    normalized === 'persona a persona b' ||
    normalized === 'lectura actual' ||
    normalized === 'lectura comparada' ||
    normalized === 'lectura guardada' ||
    normalized === 'lectura' ||
    normalized === 'conexión a' ||
    normalized === 'conexión b' ||
    normalized === 'vínculo actual' ||
    normalized === 'vínculo comparado'
  ) {
    return fallback;
  }

  return raw;
}

// 1) Insertar helper formatConnectionLabel
function formatConnectionLabel(value, fallback = 'Lectura') {
  const normalized = normalizeDisplayTitle(value, fallback);
  return String(normalized || fallback)
    .replace(/\s*✦\s*/g, ' y ')
    .replace(/\s+/g, ' ')
    .trim();
}

// 1) Insertar helper formatNarrativeText
function formatNarrativeText(text) {
  return String(text || '')
    .replace(/\s*✦\s*/g, ' y ')
    .replace(/\s+/g, ' ')
    .trim();
}

function buildBar(value, max = 100) {
  const safe = Math.max(0, Math.min(max, n(value)));
  const width = `${safe}%`;
  return {
    width,
    height: 8,
    borderRadius: 999,
    background: 'linear-gradient(90deg,#F7D26A 0%, #DAA520 100%)',
    boxShadow: '0 0 14px rgba(218,165,32,0.26)',
  };
}





// 1) REEMPLAZAR getBalanceDescription(spread)










function getPersonDisplayName(person) {
  if (!person) return '';
  if (typeof person === 'string') return person.trim();

  return String(
    person?.nickname ||
    person?.name ||
    person?.fullName ||
    person?.displayName ||
    person?.firstName ||
    person?.label ||
    person?.personName ||
    person?.inputName ||
    ''
  ).trim();
}
function isPlaceholderToken(value) {
  const token = String(value || '').trim().toLowerCase();
  if (!token) return true;

  return (
    token === 'a' ||
    token === 'b' ||
    token === 'persona a' ||
    token === 'persona b' ||
    token === 'persona a.' ||
    token === 'persona b.' ||
    token === 'person a' ||
    token === 'person b' ||
    token === 'user a' ||
    token === 'user b' ||
    token === 'usuario a' ||
    token === 'usuario b'
  );
}

function isPlaceholderPairTitle(value) {
  const raw = String(value || '').trim();
  if (!raw) return false;

  const normalized = raw
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

  if (
    normalized === 'persona a ✦ persona b' ||
    normalized === 'persona a y persona b' ||
    normalized === 'persona a persona b' ||
    normalized === 'a ✦ b' ||
    normalized === 'a y b' ||
    normalized === 'a vs b' ||
    normalized === 'a - b' ||
    normalized === 'a/b'
  ) {
    return true;
  }

  const compact = normalized
    .replace(/[|·•,]/g, ' ')
    .replace(/\s*[✦&/+\\-]\s*/g, ' y ')
    .replace(/\s+/g, ' ')
    .trim();

  const parts = compact.split(' y ').map((part) => part.trim()).filter(Boolean);
  if (parts.length !== 2) return false;

  return isPlaceholderToken(parts[0]) && isPlaceholderToken(parts[1]);
}

function extractPersonSign(person) {
  if (!person || typeof person !== 'object') return '';

  const candidates = [
    person?.zodiacSign,
    person?.sign,
    person?.sunSign,
    person?.signName,
    person?.zodiac,
    person?.signo,
    person?.signoZodiacal,
    person?.astroSign,
  ];

  for (const candidate of candidates) {
    const label = getReadableSignLabel(candidate);
    if (label) return label;
  }

  return '';
}

function getReadableSignLabel(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const map = {
    aries: 'Aries',
    tauro: 'Tauro',
    geminis: 'Géminis',
    géminis: 'Géminis',
    cancer: 'Cáncer',
    cáncer: 'Cáncer',
    leo: 'Leo',
    virgo: 'Virgo',
    libra: 'Libra',
    escorpio: 'Escorpio',
    sagitario: 'Sagitario',
    capricornio: 'Capricornio',
    acuario: 'Acuario',
    piscis: 'Piscis',
  };

  return map[raw.toLowerCase()] || raw;
}

function normalizeQuickSignKey(value) {
  const raw = String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  const map = {
    aries: 'aries',
    tauro: 'taurus',
    taurus: 'taurus',
    geminis: 'gemini',
    gemini: 'gemini',
    cancer: 'cancer',
    leo: 'leo',
    virgo: 'virgo',
    libra: 'libra',
    escorpio: 'scorpio',
    scorpio: 'scorpio',
    sagitario: 'sagittarius',
    sagittarius: 'sagittarius',
    capricornio: 'capricorn',
    capricorn: 'capricorn',
    acuario: 'aquarius',
    aquarius: 'aquarius',
    piscis: 'pisces',
    pisces: 'pisces',
  };

  return map[raw] || raw;
}

function getQuickPairFromSource(source) {
  if (!source || typeof source !== 'object') {
    return { signA: '', signB: '' };
  }

  const payload = getSavedCandidatePayload(source) || source;

  const signA = normalizeQuickSignKey(
    payload?.pair?.signA ||
      source?.pair?.signA ||
      payload?.personA?.sign ||
      payload?.personA?.sunSign ||
      source?.personA?.sign ||
      source?.personA?.sunSign ||
      ''
  );

  const signB = normalizeQuickSignKey(
    payload?.pair?.signB ||
      source?.pair?.signB ||
      payload?.personB?.sign ||
      payload?.personB?.sunSign ||
      source?.personB?.sign ||
      source?.personB?.sunSign ||
      ''
  );

  return { signA, signB };
}

function isSameQuickPair(left, right) {
  const a = getQuickPairFromSource(left);
  const b = getQuickPairFromSource(right);

  if (!a.signA || !a.signB || !b.signA || !b.signB) return false;

  const sameOrder = a.signA === b.signA && a.signB === b.signB;
  const reversedOrder = a.signA === b.signB && a.signB === b.signA;

  return sameOrder || reversedOrder;
}


function getSourceIdentity(source) {
  return {
    id: String(source?._id || source?.id || source?.savedReadingId || source?.raw?._id || source?.raw?.id || '').trim(),
    fingerprint: String(source?.fingerprint || source?.raw?.fingerprint || '').trim(),
  };
}

// Inserted helper: getDeepReadingSignature
function getDeepReadingSignature(source, fallbackPeople = null) {
  if (!source || typeof source !== 'object') return '';

  const payload = getSavedCandidatePayload(source) || source;
  const people = source?.currentPeople || fallbackPeople || null;

  const personA =
    payload?.personA ||
    payload?.people?.personA ||
    source?.personA ||
    source?.people?.personA ||
    people?.personA ||
    null;

  const personB =
    payload?.personB ||
    payload?.people?.personB ||
    source?.personB ||
    source?.people?.personB ||
    people?.personB ||
    null;

  const nameA = getPersonDisplayName(personA).toLowerCase().trim();
  const nameB = getPersonDisplayName(personB).toLowerCase().trim();
  const birthDateA = String(personA?.birthDate || '').trim();
  const birthDateB = String(personB?.birthDate || '').trim();
  const compatibility = String(getCompatibilityValue(payload || source)).trim();

  return [nameA, birthDateA, nameB, birthDateB, compatibility].join('::');
}

function isSameDeepReading(left, right, leftFallbackPeople = null, rightFallbackPeople = null) {
  const leftIdentity = getSourceIdentity(left);
  const rightIdentity = getSourceIdentity(right);

  if (leftIdentity.id && rightIdentity.id && leftIdentity.id === rightIdentity.id) {
    return true;
  }

  if (
    leftIdentity.fingerprint &&
    rightIdentity.fingerprint &&
    leftIdentity.fingerprint === rightIdentity.fingerprint
  ) {
    return true;
  }

  const leftSignature = getDeepReadingSignature(left, leftFallbackPeople);
  const rightSignature = getDeepReadingSignature(right, rightFallbackPeople);

  return Boolean(leftSignature && rightSignature && leftSignature === rightSignature);
}

function getPeopleSignsTitle(source) {
  if (!source || typeof source !== 'object') return '';

  const signPairs = [
    [source?.pair?.signA, source?.pair?.signB],
    [source?.signA, source?.signB],
    [source?.people?.signA, source?.people?.signB],
    [source?.resultSnapshot?.pair?.signA, source?.resultSnapshot?.pair?.signB],
    [source?.resultSnapshot?.people?.signA, source?.resultSnapshot?.people?.signB],
    [source?.result?.pair?.signA, source?.result?.pair?.signB],
    [source?.result?.people?.signA, source?.result?.people?.signB],
    [source?.reading?.pair?.signA, source?.reading?.pair?.signB],
    [source?.reading?.people?.signA, source?.reading?.people?.signB],
    [source?.analysis?.pair?.signA, source?.analysis?.pair?.signB],
    [source?.analysis?.people?.signA, source?.analysis?.people?.signB],
    [source?.synastry?.pair?.signA, source?.synastry?.pair?.signB],
    [source?.synastry?.people?.signA, source?.synastry?.people?.signB],
    [source?.payload?.pair?.signA, source?.payload?.pair?.signB],
    [source?.payload?.people?.signA, source?.payload?.people?.signB],
    [source?.input?.pair?.signA, source?.input?.pair?.signB],
    [source?.input?.people?.signA, source?.input?.people?.signB],
    [source?.personASign, source?.personBSign],
    [source?.personA?.zodiacSign, source?.personB?.zodiacSign],
    [source?.personA?.sign, source?.personB?.sign],
    [source?.people?.personA?.zodiacSign, source?.people?.personB?.zodiacSign],
    [source?.people?.personA?.sign, source?.people?.personB?.sign],
    [source?.currentPeople?.personA?.zodiacSign, source?.currentPeople?.personB?.zodiacSign],
    [source?.currentPeople?.personA?.sign, source?.currentPeople?.personB?.sign],
  ];

  for (const [left, right] of signPairs) {
    const a = getReadableSignLabel(left);
    const b = getReadableSignLabel(right);
    if (a && b) return `${a} y ${b}`;
  }

  const personPairs = [
    [source?.personA, source?.personB],
    [source?.people?.personA, source?.people?.personB],
    [source?.resultSnapshot?.personA, source?.resultSnapshot?.personB],
    [source?.resultSnapshot?.people?.personA, source?.resultSnapshot?.people?.personB],
    [source?.result?.personA, source?.result?.personB],
    [source?.result?.people?.personA, source?.result?.people?.personB],
    [source?.reading?.personA, source?.reading?.personB],
    [source?.reading?.people?.personA, source?.reading?.people?.personB],
    [source?.analysis?.personA, source?.analysis?.personB],
    [source?.analysis?.people?.personA, source?.analysis?.people?.personB],
    [source?.synastry?.personA, source?.synastry?.personB],
    [source?.synastry?.people?.personA, source?.synastry?.people?.personB],
    [source?.payload?.personA, source?.payload?.personB],
    [source?.payload?.people?.personA, source?.payload?.people?.personB],
    [source?.input?.personA, source?.input?.personB],
    [source?.input?.people?.personA, source?.input?.people?.personB],
  ];

  for (const [leftPerson, rightPerson] of personPairs) {
    const a = extractPersonSign(leftPerson);
    const b = extractPersonSign(rightPerson);
    if (a && b) return `${a} y ${b}`;
  }

  return '';
}

function getConnectionNamesTitle(source, fallback = 'Este vínculo') {
  const byPeople = getPairDisplayTitle(source, '');
  if (byPeople && !isGenericReadingLabel(byPeople) && !isPlaceholderPairTitle(byPeople)) {
    return byPeople;
  }
  return fallback;
}

function getDeepConnectionNamesTitle(source, fallback = '') {
  if (!source || typeof source !== 'object') return fallback;

  return (
    getConnectionNamesTitle(source, '') ||
    getConnectionNamesTitle(source?.raw, '') ||
    getConnectionNamesTitle(source?.resultSnapshot, '') ||
    getConnectionNamesTitle(source?.result, '') ||
    getConnectionNamesTitle(source?.reading, '') ||
    getConnectionNamesTitle(source?.analysis, '') ||
    getConnectionNamesTitle(source?.synastry, '') ||
    getConnectionNamesTitle(source?.payload, '') ||
    getConnectionNamesTitle(source?.people, '') ||
    getConnectionNamesTitle(source?.input, '') ||
    fallback
  );
}

function getConnectionSignsTitle(source, fallback = 'Este vínculo') {
  const bySigns = getPeopleSignsTitle(source);
  if (bySigns) return bySigns;
  return fallback;
}

function getDeepConnectionSignsTitle(source, fallback = '') {
  if (!source || typeof source !== 'object') return fallback;

  return (
    getConnectionSignsTitle(source, '') ||
    getConnectionSignsTitle(source?.raw, '') ||
    getConnectionSignsTitle(source?.resultSnapshot, '') ||
    getConnectionSignsTitle(source?.result, '') ||
    getConnectionSignsTitle(source?.reading, '') ||
    getConnectionSignsTitle(source?.analysis, '') ||
    getConnectionSignsTitle(source?.synastry, '') ||
    getConnectionSignsTitle(source?.payload, '') ||
    getConnectionSignsTitle(source?.people, '') ||
    getConnectionSignsTitle(source?.input, '') ||
    fallback
  );
}

function getPairDisplayTitle(source, fallback = '') {
  if (!source || typeof source !== 'object') return fallback || '';

  const candidates = [
    [source?.personA, source?.personB],
    [source?.people?.personA, source?.people?.personB],
    [source?.resultSnapshot?.personA, source?.resultSnapshot?.personB],
    [source?.input?.personA, source?.input?.personB],
    [source?.inputs?.personA, source?.inputs?.personB],
    [source?.person1, source?.person2],
    [source?.people?.person1, source?.people?.person2],
    [source?.a, source?.b],
    [source?.leftPerson, source?.rightPerson],
  ];

  for (const [left, right] of candidates) {
    const a = getPersonDisplayName(left);
    const b = getPersonDisplayName(right);
    if (a && b) return `${a} y ${b}`;
  }

  const flatPairs = [
    [source?.personAName, source?.personBName],
    [source?.personA_name, source?.personB_name],
    [source?.personAFullName, source?.personBFullName],
    [source?.personADisplayName, source?.personBDisplayName],
    [source?.personANickname, source?.personBNickname],
    [source?.nameA, source?.nameB],
    [source?.firstNameA, source?.firstNameB],
    [source?.leftName, source?.rightName],
    [source?.personOneName, source?.personTwoName],
  ];

  for (const [left, right] of flatPairs) {
    const a = getPersonDisplayName(left);
    const b = getPersonDisplayName(right);
    if (a && b) return `${a} y ${b}`;
  }

  const nestedTitleCandidates = [
    source?.peopleTitle,
    source?.pairTitle,
    source?.displayTitle,
    source?.relationshipTitle,
    source?.connectionTitle,
  ];

  for (const candidate of nestedTitleCandidates) {
    const title = String(candidate || '').trim();
    if (title) return title;
  }

  return fallback || '';
}


function getSavedCandidatePayload(item) {
  if (!item || typeof item !== 'object') return null;

  return (
    item?.resultSnapshot ||
    item?.result ||
    item?.reading ||
    item?.analysis ||
    item?.synastry ||
    item?.payload ||
    item?.raw?.resultSnapshot ||
    item?.raw?.result ||
    item?.raw?.reading ||
    item?.raw?.analysis ||
    item?.raw?.synastry ||
    item?.raw?.payload ||
    item?.raw ||
    null
  );
}

function normalizeSavedCandidate(item, index) {
  const id = String(
    item?._id ||
    item?.id ||
    item?.savedReadingId ||
    item?.raw?._id ||
    item?.raw?.id ||
    ''
  ).trim();

  const payload = getSavedCandidatePayload(item) || {};

  const readableFallback =
    getDeepConnectionNamesTitle(item, '') ||
    getDeepConnectionNamesTitle(payload, '') ||
    getDeepConnectionSignsTitle(item, '') ||
    getDeepConnectionSignsTitle(payload, '') ||
    `Lectura guardada ${index + 1}`;

  const explicitTitle = String(item?.title || payload?.title || '').trim();

  const title = formatConnectionLabel(
    explicitTitle,
    readableFallback
  );

  return {
    id,
    title: title || readableFallback,
    compatibility: n(
      payload?.compatibility?.masterScore ??
      payload?.compatibility?.master ??
      payload?.compatibilityScore ??
      payload?.compatibility?.score ??
      payload?.compatibility ??
      item?.compatibilityScore ??
      item?.compatibility
    ),
    personA: item?.personA || payload?.personA || payload?.people?.personA || null,
    personB: item?.personB || payload?.personB || payload?.people?.personB || null,
    raw: item,
  };
}

function aspectLine(item) {
  if (!item || typeof item !== 'object') return 'Aspecto relevante';
  return String(item.title || item.label || item.name || item.aspect || 'Aspecto relevante').trim();
}

function getCompatibilityValue(reading) {
  return n(
    reading?.compatibility?.masterScore ??
    reading?.compatibility?.master ??
    reading?.compatibilityScore ??
    reading?.compatibility?.score ??
    reading?.compatibility
  );
}

function getDimensionValue(reading, key) {
  return n(
    reading?.dimensions?.[key]?.score ??
    reading?.dimensions?.[key] ??
    reading?.dimensionScores?.[key]?.score ??
    reading?.dimensionScores?.[key]
  );
}

function getReadingScores(reading) {
  return {
    attraction: getDimensionValue(reading, 'attraction'),
    emotional: getDimensionValue(reading, 'emotional'),
    communication: getDimensionValue(reading, 'communication'),
    stability: getDimensionValue(reading, 'stability'),
    karmic: getDimensionValue(reading, 'karmic'),
  };
}







function normalizeVerdictWinner(flag, fallbackA, fallbackB) {
  if (flag === 'readingA') return fallbackA;
  if (flag === 'readingB') return fallbackB;
  return 'Empate';
}

function isGenericReadingLabel(value) {
  const normalized = String(value || '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

  if (isPlaceholderPairTitle(value)) return true;

  return (
    !normalized ||
    normalized === 'lectura actual' ||
    normalized === 'lectura comparada' ||
    normalized === 'lectura guardada' ||
    normalized === 'conexión a' ||
    normalized === 'conexión b' ||
    normalized === 'vínculo actual' ||
    normalized === 'vínculo comparado' ||
    normalized === 'persona a y persona b' ||
    normalized === 'persona a ✦ persona b' ||
    normalized === 'persona a persona b'
  );
}

function replaceGenericLabels(text, currentLabel, comparedLabel) {
  const raw = String(text || '').trim();
  if (!raw) return '';

  return raw
    .replaceAll('Lectura actual', currentLabel)
    .replaceAll('Vínculo actual', currentLabel)
    .replaceAll('Conexión A', currentLabel)
    .replaceAll('Lectura comparada', comparedLabel)
    .replaceAll('Vínculo comparado', comparedLabel)
    .replaceAll('Conexión B', comparedLabel)
    .replaceAll('Persona A y Persona B', comparedLabel)
    .replaceAll('Persona A ✦ Persona B', comparedLabel)
    .replaceAll('persona a y persona b', comparedLabel)
    .replaceAll('persona a ✦ persona b', comparedLabel)
    .replaceAll('a y b', comparedLabel)
    .replaceAll('a ✦ b', comparedLabel)
    .replaceAll('A y B', comparedLabel)
    .replaceAll('A ✦ B', comparedLabel);
}

function uniqueNonEmptyLines(values = []) {
  const seen = new Set();
  return safeArray(values).filter((value) => {
    const text = String(value || '').trim();
    if (!text) return false;
    const key = text.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// 3) REEMPLAZAR buildExecutiveSummary(...)
function buildExecutiveSummary({
  titleA,
  titleB,
  highlights,
  verdictClosing,
  narrativeClosing,
  radarInsight,
}) {
  const safeTitleA = formatConnectionLabel(titleA, 'Este vínculo');
  const safeTitleB = formatConnectionLabel(titleB, 'Vínculo comparado');

  const strongest = highlights?.strongestBond === 'readingA'
    ? `${safeTitleA} sostiene la estructura relacional más sólida en el conjunto.`
    : highlights?.strongestBond === 'readingB'
      ? `${safeTitleB} sostiene la estructura relacional más sólida en el conjunto.`
      : 'Ambos vínculos muestran una fuerza global muy pareja dentro de esta comparativa.';

  const preferred = uniqueNonEmptyLines([
    formatNarrativeText(replaceGenericLabels(verdictClosing, safeTitleA, safeTitleB)),
    formatNarrativeText(replaceGenericLabels(narrativeClosing, safeTitleA, safeTitleB)),
    formatNarrativeText(strongest),
    formatNarrativeText(replaceGenericLabels(radarInsight, safeTitleA, safeTitleB)),
  ]);

  return preferred[0] || 'Esta comparativa ordena con claridad dónde vive la mayor fuerza de cada conexión y qué vínculo logra una arquitectura más completa.';
}

function buildNarrativeBulletItems({ bullets, titleA, titleB, verdictClosing }) {
  const safeTitleA = formatConnectionLabel(titleA, 'Este vínculo');
  const safeTitleB = formatConnectionLabel(titleB, 'Vínculo comparado');
  const replacedVerdict = replaceGenericLabels(String(verdictClosing || '').trim(), safeTitleA, safeTitleB);

  const redundantPatterns = [
    /compatibilidad global/i,
    /resonancia emocional/i,
    /din[aá]mica comunicacional/i,
    /impronta k[aá]rmica/i,
    /huella k[aá]rmica/i,
    /se perfila como el v[ií]nculo m[aá]s completo/i,
    /lidera la compatibilidad global/i,
    /m[aá]s alta que/i,
    /esta comparativa revela d[oó]nde vive/i,
  ];

  const cleaned = uniqueNonEmptyLines(
    safeArray(bullets).map((bullet) => {
      const text = replaceGenericLabels(String(bullet || '').trim(), safeTitleA, safeTitleB);
      if (!text) return '';
      if (replacedVerdict && text === replacedVerdict) return '';
      if (redundantPatterns.some((pattern) => pattern.test(text))) return '';
      return formatNarrativeText(formatConnectionLabel(text, text));
    })
  );

  return cleaned.slice(0, 2);
}

function buildRadarData(scoresA = {}, scoresB = {}) {
  return [
    {
      dimension: 'Atracción',
      current: n(scoresA.attraction),
      compared: n(scoresB.attraction),
    },
    {
      dimension: 'Emoción',
      current: n(scoresA.emotional),
      compared: n(scoresB.emotional),
    },
    {
      dimension: 'Comunicación',
      current: n(scoresA.communication),
      compared: n(scoresB.communication),
    },
    {
      dimension: 'Estabilidad',
      current: n(scoresA.stability),
      compared: n(scoresB.stability),
    },
    {
      dimension: 'Kármico',
      current: n(scoresA.karmic),
      compared: n(scoresB.karmic),
    },
  ];
}

function buildRadarInsight(scoresA = {}, scoresB = {}, titleA = 'Vínculo actual', titleB = 'Vínculo comparado') {
  const currentTotal =
    n(scoresA.attraction) +
    n(scoresA.emotional) +
    n(scoresA.communication) +
    n(scoresA.stability) +
    n(scoresA.karmic);

  const comparedTotal =
    n(scoresB.attraction) +
    n(scoresB.emotional) +
    n(scoresB.communication) +
    n(scoresB.stability) +
    n(scoresB.karmic);

  if (currentTotal === comparedTotal) {
    return `Ambos vínculos muestran una huella energética muy pareja. La diferencia aparece más en el tipo de distribución que en la intensidad total.`;
  }

  return currentTotal > comparedTotal
    ? `${titleA} despliega una presencia energética más amplia en el mapa, mientras ${titleB} concentra su fuerza en áreas más específicas.`
    : `${titleB} despliega una presencia energética más amplia en el mapa, mientras ${titleA} concentra su fuerza en áreas más específicas.`;
}

function polarToCartesian(cx, cy, radius, angleDeg) {
  const angleRad = ((angleDeg - 90) * Math.PI) / 180;
  return {
    x: cx + radius * Math.cos(angleRad),
    y: cy + radius * Math.sin(angleRad),
  };
}

function buildRadarPolygonPoints(data = [], key, cx = 180, cy = 180, radius = 120) {
  if (!Array.isArray(data) || !data.length) return '';
  return data
    .map((item, index) => {
      const angle = (360 / data.length) * index;
      const value = Math.max(0, Math.min(100, n(item?.[key])));
      const point = polarToCartesian(cx, cy, (value / 100) * radius, angle);
      return `${point.x},${point.y}`;
    })
    .join(' ');
}

function buildRadarAxisMeta(data = [], cx = 180, cy = 180, radius = 120) {
  if (!Array.isArray(data)) return [];
  return data.map((item, index) => {
    const angle = (360 / Math.max(1, data.length)) * index;
    const end = polarToCartesian(cx, cy, radius, angle);
    const label = polarToCartesian(cx, cy, radius + 24, angle);
    return {
      key: item?.dimension || `axis-${index}`,
      dimension: item?.dimension || '',
      end,
      label,
    };
  });
}

function buildRadarRingPoints(totalAxes, ringRadius, cx = 180, cy = 180) {
  if (!totalAxes) return '';
  return Array.from({ length: totalAxes }, (_, index) => {
    const angle = (360 / totalAxes) * index;
    const point = polarToCartesian(cx, cy, ringRadius, angle);
    return `${point.x},${point.y}`;
  }).join(' ');
}

function wrapCanvasText(ctx, text, maxWidth) {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  if (!words.length) return [''];

  const lines = [];
  let current = words[0];

  for (let i = 1; i < words.length; i += 1) {
    const candidate = `${current} ${words[i]}`;
    if (ctx.measureText(candidate).width <= maxWidth) current = candidate;
    else {
      lines.push(current);
      current = words[i];
    }
  }

  lines.push(current);
  return lines;
}

function truncateCanvasText(ctx, text, maxWidth) {
  const raw = String(text || '').trim();
  if (!raw) return '';
  if (ctx.measureText(raw).width <= maxWidth) return raw;

  let result = raw;
  while (result.length > 1 && ctx.measureText(`${result}…`).width > maxWidth) {
    result = result.slice(0, -1);
  }
  return `${result}…`;
}

function drawRoundedRect(ctx, x, y, width, height, radius) {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + width, y, x + width, y + height, r);
  ctx.arcTo(x + width, y + height, x, y + height, r);
  ctx.arcTo(x, y + height, x, y, r);
  ctx.arcTo(x, y, x + width, y, r);
  ctx.closePath();
}

async function canvasToBlob(canvas) {
  return await new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/png');
  });
}

function buildComparisonShareSummary({
  titleA,
  titleB,
  readingA,
  readingB,
  highlights,
  verdictClosing,
  narrativeClosing,
}) {
  return [
    '✦ Comparativa Cósmica · Zodiacus',
    `${titleA} vs ${titleB}`,
    `Compatibilidad global: ${getCompatibilityValue(readingA)} vs ${getCompatibilityValue(readingB)}`,
    `Más atracción: ${replaceGenericLabels(getWinnerLabel(highlights?.mostPassionateBond, titleA, titleB), titleA, titleB)}`,
    `Más estabilidad: ${replaceGenericLabels(getWinnerLabel(highlights?.mostStableBond, titleA, titleB), titleA, titleB)}`,
    `Más resonancia emocional: ${replaceGenericLabels(getWinnerLabel(highlights?.mostEmotionalBond, titleA, titleB), titleA, titleB)}`,
    `Más intensidad kármica: ${replaceGenericLabels(getWinnerLabel(highlights?.mostKarmicBond, titleA, titleB), titleA, titleB)}`,
    verdictClosing || narrativeClosing || 'Compará vínculos en Zodiacus.',
  ].filter(Boolean).join('\n');
}

function downloadBlobFile(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function openSharePopup(url, name = 'share-window') {
  const width = 720;
  const height = 760;
  const left = Math.max(0, Math.round((window.screen.width - width) / 2));
  const top = Math.max(0, Math.round((window.screen.height - height) / 2));
  window.open(
    url,
    name,
    `popup=yes,width=${width},height=${height},left=${left},top=${top},noopener,noreferrer`
  );
}



export default function SynastryComparisonScreen() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    clearLegacySavedSynastryStorage();
  }, []);
    const routeState = location.state || {};
      const routeMode = String(routeState.readingMode || '').trim().toLowerCase();

  const currentReading = routeState.currentReading || null;
const currentPeople = routeState.currentPeople || null;
const [selectedId, setSelectedId] = useState('');
const [dismissedCandidateIds, setDismissedCandidateIds] = useState(() => readDismissedComparisonCandidateIds());

const savedCandidates = useMemo(() => {
    const rawItems = safeArray(routeState.savedCandidates);

    return rawItems
      .filter((item) => {
        if (!routeMode) return true;

        const payload = getSavedCandidatePayload(item) || {};

        const mode = String(
          item?.mode ||
            item?.readingType ||
            item?.calculationMode ||
            item?.raw?.mode ||
            payload?.mode ||
            payload?.readingType ||
            payload?.calculationMode ||
            ''
        )
          .trim()
          .toLowerCase();

        const source = String(
          item?.source ||
            item?.sourceType ||
            item?.raw?.source ||
            payload?.source ||
            payload?.sourceType ||
            ''
        )
          .trim()
          .toLowerCase();

        const looksQuick =
          mode === 'quick' ||
          source.includes('quick') ||
          source.includes('solar') ||
          Boolean(payload?.pair?.signA && payload?.pair?.signB) ||
          Boolean(item?.pair?.signA && item?.pair?.signB) ||
          Boolean(item?.raw?.pair?.signA && item?.raw?.pair?.signB) ||
          Boolean(item?.result?.pair?.signA && item?.result?.pair?.signB) ||
          Boolean(item?.reading?.pair?.signA && item?.reading?.pair?.signB);

        const inferredMode = looksQuick ? 'quick' : 'deep';
        return inferredMode === routeMode;
      })
      .map(normalizeSavedCandidate)
.filter((x) => String(x?.id || '').trim())
.filter((item) => !dismissedCandidateIds.includes(String(item?.id || '').trim()))
      .filter((item) => {
        const candidateSource = item?.raw || item;
        const currentIdentity = getSourceIdentity(currentReading || {});
        const candidateIdentity = getSourceIdentity(candidateSource);

        if (currentIdentity.id && candidateIdentity.id && currentIdentity.id === candidateIdentity.id) {
          return false;
        }

        if (
          currentIdentity.fingerprint &&
          candidateIdentity.fingerprint &&
          currentIdentity.fingerprint === candidateIdentity.fingerprint
        ) {
          return false;
        }

        if (routeMode === 'quick') {
          if (isSameQuickPair(currentReading, candidateSource)) {
            return false;
          }
          return true;
        }

        if (routeMode === 'deep') {
          if (isSameDeepReading(currentReading, candidateSource, currentPeople, null)) {
            return false;
          }
        }

        return true;
      });
  }, [routeState.savedCandidates, routeMode, currentReading, currentPeople, dismissedCandidateIds]);

  const currentTitle = useMemo(() => {
    const readableFallback =
      getDeepConnectionNamesTitle(currentReading, '') ||
      getDeepConnectionNamesTitle(currentPeople, '') ||
      getDeepConnectionSignsTitle(currentReading, '') ||
      getDeepConnectionSignsTitle(currentPeople, '') ||
      'Este vínculo';

    return formatConnectionLabel(
      String(currentReading?.title || readableFallback || 'Este vínculo').trim(),
      readableFallback || 'Este vínculo'
    );
  }, [currentPeople, currentReading]);
      function handleBack() {
    const from = location.state?.from;

    if (from === 'quick') {
      navigate('/synastry', {
        state: { mode: 'quick', initialTab: 'quick' },
      });
      return;
    }

    if (from === 'deep') {
      navigate('/synastry', {
        state: { openDeepMode: true, initialTab: 'deep', mode: 'deep' },
      });
      return;
    }

    if (from === 'library') {
      navigate('/saved-synastries');
      return;
    }

    navigate('/synastry');
  }

  const [loadingCompare, setLoadingCompare] = useState(false);
  const [error, setError] = useState('');
  const [comparison, setComparison] = useState(null);
  const [comparedSavedSynastry, setComparedSavedSynastry] = useState(null);
  const [shareStatus, setShareStatus] = useState('');
const [sharePanelOpen, setSharePanelOpen] = useState(false);

  const selectedCandidate = useMemo(
    () => savedCandidates.find((item) => item.id === selectedId) || null,
    [savedCandidates, selectedId]
  );

  useEffect(() => {
    if (!selectedId) return;
    if (savedCandidates.some((item) => item.id === selectedId)) return;
    setSelectedId('');
  }, [selectedId, savedCandidates]);

  useEffect(() => {
    writeDismissedComparisonCandidateIds(dismissedCandidateIds);
  }, [dismissedCandidateIds]);

  const currentMaster = getCompatibilityValue(currentReading);

  async function handleCompare() {
    setError('');
    setComparison(null);
    setComparedSavedSynastry(null);

    if (!currentReading) {
      setError(synText('No hay una lectura actual disponible para comparar.', 'There is no current reading available to compare.'));
      return;
    }

        if (!selectedCandidate) {
      setError(synText('La lectura seleccionada no está disponible para este modo de comparación.', 'The selected reading is not available in this comparison mode.'));
      return;
    }
    if (routeMode === 'quick' && savedCandidates.length < 1) {
      setError(synText('Necesitás al menos otra lectura quick guardada para comparar esta compatibilidad solar.', 'You need at least one other saved quick reading to compare this solar compatibility.'));
      return;
    }

    setLoadingCompare(true);
    try {
      const data = await compareSavedSynastries(currentReading, selectedId);

      if (!data?.comparison) {
        setError(synText('No se pudo generar la comparativa cósmica.', 'The cosmic comparison could not be generated.'));
        return;
      }

      setComparison(data.comparison);
      setComparedSavedSynastry(data.comparedSavedSynastry || null);
       } catch (err) {
  const status = Number(err?.response?.status || err?.status || 0);
  const backendError = String(err?.response?.data?.error || err?.message || '').trim();
  const normalizedError = backendError.toLowerCase();

  const staleReadingError =
    status === 404 ||
    normalizedError.includes('saved synastry not found') ||
    normalizedError.includes('sinastria guardada no encontrada') ||
    normalizedError.includes('lectura guardada no encontrada');

  if (staleReadingError && selectedCandidate?.id) {
    const staleId = String(selectedCandidate.id || '').trim();

    if (staleId) {
      purgeSavedCandidateFromComparisonStorage(staleId);
      setDismissedCandidateIds((prev) =>
        prev.includes(staleId) ? prev : [...prev, staleId]
      );
    }

    setSelectedId('');
    setComparedSavedSynastry(null);
    setComparison(null);
    setError(synText('Esa lectura ya no existe en la biblioteca actual. La removimos de esta lista; elegí otra lectura guardada.', 'That reading no longer exists in your library. Choose another saved reading.'));
    return;
  }

  if (
    normalizedError.includes('quick con una lectura deep') ||
    normalizedError.includes('quick con quick o deep con deep')
  ) {
    setError(synText('No se puede comparar una lectura quick con una deep. Compará quick con quick o deep con deep.', 'A quick reading cannot be compared with a deep reading. Compare quick with quick or deep with deep.'));
  } else {
    setError(synText(backendError || 'Ocurrió un error al comparar las dos sinastrías.', 'An error occurred while comparing the two synastry readings.'));
  }
} finally {
      setLoadingCompare(false);
    }
  }

  async function handleShareComparison() {
  if (!comparison) return;
  setShareStatus('');
  setSharePanelOpen((prev) => !prev);
}

  async function generateComparisonCardBlob() {
    if (!comparison) return null;

    try {
      const canvas = document.createElement('canvas');
      canvas.width = 1080;
      canvas.height = 1920;
      const ctx = canvas.getContext('2d');

      if (!ctx) return null;

      const bg = ctx.createLinearGradient(0, 0, 0, canvas.height);
      bg.addColorStop(0, '#22043A');
      bg.addColorStop(0.22, '#2E0A4F');
      bg.addColorStop(0.5, '#3B0F63');
      bg.addColorStop(0.78, '#4B0082');
      bg.addColorStop(1, '#DAA520');
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const overlay = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      overlay.addColorStop(0, 'rgba(255,255,255,0.05)');
      overlay.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = overlay;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const shellX = 56;
      const shellY = 72;
      const shellW = canvas.width - 112;
      const shellH = canvas.height - 144;

      ctx.save();
      drawRoundedRect(ctx, shellX, shellY, shellW, shellH, 34);
      ctx.fillStyle = 'rgba(16,0,36,0.72)';
      ctx.fill();
      ctx.strokeStyle = 'rgba(218,165,32,0.36)';
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.restore();

      let y = shellY + 62;
      const left = shellX + 54;
      const contentW = shellW - 108;

      ctx.fillStyle = '#F7D26A';
      ctx.font = '900 26px Arial';
      ctx.fillText('✦ COMPARATIVA CÓSMICA', left, y);

      y += 44;
      ctx.fillStyle = '#FFF4CC';
      ctx.font = '900 58px Arial';
      ctx.fillText('Zodiacus', left, y);

      y += 52;
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      ctx.font = '700 34px Arial';
      ctx.fillText(truncateCanvasText(ctx, `${titleA} vs ${titleB}`, contentW), left, y);

      y += 46;
      ctx.fillStyle = 'rgba(255,255,255,0.78)';
      ctx.font = '500 24px Arial';
      const subLines = wrapCanvasText(
        ctx,
        'Una lectura premium para ver qué vínculo destaca por atracción, estabilidad, emoción, comunicación e intensidad kármica.',
        contentW
      );
      subLines.slice(0, 2).forEach((line) => {
        ctx.fillText(line, left, y);
        y += 32;
      });

      y += 22;

      const pillW = (contentW - 24) / 3;
      const pillH = 132;
      const pillY = y;
      const scores = [
        {
          label: 'Vínculo actual',
          title: signTitleA,
          value: String(getCompatibilityValue(readingA)),
          glow: '#DAA520',
          fill: 'rgba(218,165,32,0.14)',
        },
        {
          label: 'Vínculo comparado',
          title: signTitleB,
          value: String(getCompatibilityValue(readingB)),
          glow: '#C084FC',
          fill: 'rgba(124,58,237,0.18)',
        },
        {
          label: 'Diferencia',
          title: 'Diferencia de compatibilidad',
          value: formatSignedDelta(delta?.compatibility),
          glow: getDeltaTone(delta?.compatibility),
          fill: 'rgba(255,255,255,0.08)',
        },
      ];

      scores.forEach((item, index) => {
        const x = left + index * (pillW + 12);
        ctx.save();
        drawRoundedRect(ctx, x, pillY, pillW, pillH, 24);
        ctx.fillStyle = item.fill;
        ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,0.10)';
        ctx.lineWidth = 1.5;
        ctx.stroke();
        ctx.restore();

        ctx.fillStyle = '#F7D26A';
        ctx.font = '900 20px Arial';
        ctx.fillText(item.label, x + 20, pillY + 34);

        ctx.fillStyle = '#FFF4CC';
        ctx.font = '800 24px Arial';
        ctx.fillText(truncateCanvasText(ctx, item.title, pillW - 40), x + 20, pillY + 66);

        ctx.fillStyle = item.glow;
        ctx.font = '900 42px Arial';
        ctx.fillText(item.value, x + 20, pillY + 110);
      });

      y = pillY + pillH + 38;

      ctx.fillStyle = '#F7D26A';
      ctx.font = '900 24px Arial';
      ctx.fillText('ADN ENERGÉTICO DEL VÍNCULO', left, y);

      y += 30;
      ctx.fillStyle = 'rgba(255,255,255,0.76)';
      ctx.font = '500 22px Arial';
      const radarLines = wrapCanvasText(ctx, radarInsight, contentW);
      radarLines.slice(0, 2).forEach((line) => {
        y += 28;
        ctx.fillText(line, left, y);
      });

      const chartX = left + 30;
      const chartY = y + 54;
      const cx = chartX + 190;
      const cy = chartY + 190;
      const radius = 138;

      [0.25, 0.5, 0.75, 1].forEach((ratio) => {
        const points = buildRadarRingPoints(radarData.length, radius * ratio, cx, cy);
        ctx.save();
        ctx.strokeStyle = 'rgba(255,255,255,0.14)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        points.split(' ').forEach((pair, index) => {
          const [px, py] = pair.split(',').map(Number);
          if (index === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        });
        ctx.closePath();
        ctx.stroke();
        ctx.restore();
      });

      radarAxes.forEach((axis, index) => {
        const angle = (360 / radarAxes.length) * index;
        const end = polarToCartesian(cx, cy, radius, angle);
        const label = polarToCartesian(cx, cy, radius + 24, angle);

        ctx.strokeStyle = 'rgba(255,255,255,0.12)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.lineTo(end.x, end.y);
        ctx.stroke();

        ctx.fillStyle = '#FFF4CC';
        ctx.font = '800 18px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(axis.dimension, label.x, label.y);
      });

      ctx.textAlign = 'left';
      ctx.textBaseline = 'alphabetic';

      const currentPoints = buildRadarPolygonPoints(radarData, 'current', cx, cy, radius)
        .split(' ')
        .map((pair) => pair.split(',').map(Number));

      const comparedPoints = buildRadarPolygonPoints(radarData, 'compared', cx, cy, radius)
        .split(' ')
        .map((pair) => pair.split(',').map(Number));

      ctx.save();
      ctx.beginPath();
      currentPoints.forEach(([px, py], index) => {
        if (index === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      });
      ctx.closePath();
      ctx.fillStyle = 'rgba(218,165,32,0.28)';
      ctx.strokeStyle = '#DAA520';
      ctx.lineWidth = 4;
      ctx.fill();
      ctx.stroke();
      ctx.restore();

      ctx.save();
      ctx.beginPath();
      comparedPoints.forEach(([px, py], index) => {
        if (index === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      });
      ctx.closePath();
      ctx.fillStyle = 'rgba(124,58,237,0.18)';
      ctx.strokeStyle = '#C084FC';
      ctx.lineWidth = 4;
      ctx.fill();
      ctx.stroke();
      ctx.restore();

      ctx.fillStyle = '#FFF4CC';
      ctx.beginPath();
      ctx.arc(cx, cy, 6, 0, Math.PI * 2);
      ctx.fill();

      const sideX = chartX + 430;
      const sideY = chartY + 8;
      const sideW = contentW - 430;

      const topFacts = [
        {
          label: 'Más atracción',
          value: replaceGenericLabels(getWinnerLabel(highlights?.mostPassionateBond, titleA, titleB), titleA, titleB),
        },
        {
          label: 'Más estabilidad',
          value: replaceGenericLabels(getWinnerLabel(highlights?.mostStableBond, titleA, titleB), titleA, titleB),
        },
        {
          label: 'Más emoción',
          value: replaceGenericLabels(getWinnerLabel(highlights?.mostEmotionalBond, titleA, titleB), titleA, titleB),
        },
        {
          label: 'Más kármico',
          value: replaceGenericLabels(getWinnerLabel(highlights?.mostKarmicBond, titleA, titleB), titleA, titleB),
        },
      ];

      topFacts.forEach((fact, index) => {
        const boxY = sideY + index * 102;
        ctx.save();
        drawRoundedRect(ctx, sideX, boxY, sideW, 84, 20);
        ctx.fillStyle = 'rgba(255,255,255,0.05)';
        ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,0.10)';
        ctx.lineWidth = 1.5;
        ctx.stroke();
        ctx.restore();

        ctx.fillStyle = '#F7D26A';
        ctx.font = '900 18px Arial';
        ctx.fillText(fact.label, sideX + 18, boxY + 30);

        ctx.fillStyle = '#FFF4CC';
        ctx.font = '800 24px Arial';
        ctx.fillText(truncateCanvasText(ctx, fact.value, sideW - 36), sideX + 18, boxY + 62);
      });

      const verdictY = chartY + 440;
      ctx.save();
      drawRoundedRect(ctx, left, verdictY, contentW, 222, 24);
      ctx.fillStyle = 'rgba(255,255,255,0.05)';
      ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.10)';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.restore();

      ctx.fillStyle = '#F7D26A';
      ctx.font = '900 22px Arial';
      ctx.fillText('VEREDICTO RELACIONAL', left + 22, verdictY + 36);

      ctx.fillStyle = '#FFF4CC';
      ctx.font = '800 24px Arial';
      ctx.fillText(`Amor y sostén: ${truncateCanvasText(ctx, verdictLove, contentW - 210)}`, left + 22, verdictY + 76);
      ctx.fillText(`Pasión: ${truncateCanvasText(ctx, verdictPassion, contentW - 160)}`, left + 22, verdictY + 110);
      ctx.fillText(`Crecimiento: ${truncateCanvasText(ctx, verdictGrowth, contentW - 190)}`, left + 22, verdictY + 144);
      ctx.fillText(`Armonía global: ${truncateCanvasText(ctx, verdictHarmony, contentW - 220)}`, left + 22, verdictY + 178);

      const footerY = verdictY + 260;
      ctx.fillStyle = 'rgba(255,255,255,0.88)';
      ctx.font = '700 24px Arial';
      const footerText = verdictClosing || narrativeClosing || 'Compará vínculos en Zodiacus.';
      const footerLines = wrapCanvasText(ctx, footerText, contentW);
      footerLines.slice(0, 3).forEach((line, index) => {
        ctx.fillText(line, left, footerY + index * 32);
      });

      ctx.fillStyle = 'rgba(255,255,255,0.62)';
      ctx.font = '700 18px Arial';
      ctx.fillText('zodiacus.app · Comparativa premium de sinastrías', left, shellY + shellH - 24);

      return await canvasToBlob(canvas);
    } catch {
      return null;
    }
  }

    
async function handleCopyComparisonText() {
  if (!comparison) return;

  const summary = buildComparisonShareSummary({
    titleA,
    titleB,
    readingA,
    readingB,
    highlights,
    verdictClosing,
    narrativeClosing,
  });

  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(summary);
      setShareStatus('Resumen copiado al portapapeles.');
      return;
    }
    setShareStatus('No fue posible copiar el resumen desde este dispositivo.');
  } catch {
    setShareStatus('No se pudo copiar el resumen.');
  }
}

function handleShareWhatsApp() {
  const summary = buildComparisonShareSummary({
    titleA,
    titleB,
    readingA,
    readingB,
    highlights,
    verdictClosing,
    narrativeClosing,
  });
  openSharePopup(`https://wa.me/?text=${encodeURIComponent(summary)}`, 'share-whatsapp');
}

async function handleShareInstagram() {
  openSharePopup('https://www.instagram.com/', 'share-instagram');
  setShareStatus('Instagram se abrió en una ventana emergente. La imagen no se puede precargar automáticamente desde la web, así que si querés subir la card usá también “Descargar archivo”.');
}

function handleShareFacebook() {
  openSharePopup(
    `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent('https://zodiacus.app')}`,
    'share-facebook'
  );
  setShareStatus('Facebook compartirá el enlace. Para imagen, usá Instagram o descargá la card.');
}

function handleShareTelegram() {
  const summary = buildComparisonShareSummary({
    titleA,
    titleB,
    readingA,
    readingB,
    highlights,
    verdictClosing,
    narrativeClosing,
  });
  openSharePopup(`https://t.me/share/url?url=${encodeURIComponent('https://zodiacus.app')}&text=${encodeURIComponent(summary)}`, 'share-telegram');
}

function handleShareX() {
  const summary = buildComparisonShareSummary({
    titleA,
    titleB,
    readingA,
    readingB,
    highlights,
    verdictClosing,
    narrativeClosing,
  });
  openSharePopup(`https://twitter.com/intent/tweet?text=${encodeURIComponent(summary)}`, 'share-x');
}

async function handleDownloadComparisonCard() {
  const blob = await generateComparisonCardBlob();
  if (!blob) {
    setShareStatus('No se pudo descargar la card de la comparativa.');
    return;
  }
  downloadBlobFile(blob, 'zodiacus-comparativa-cosmica.png');
  setShareStatus('Card descargada con éxito.');
}
    
  const readingA = comparison?.readingA || null;
  const readingB = comparison?.readingB || null;
  const delta = comparison?.delta || null;
  const highlights = comparison?.highlights || null;
  const narrative = comparison?.narrative || null;
  
  const backendVerdict = comparison?.verdict || null;

// 3) Reemplazo robusto de titleA / titleB
// 3) Reemplazo robusto de titleA / titleB
const rawTitleA = String(readingA?.title || '').trim();

const fallbackTitleA =
  currentTitle ||
  getDeepConnectionNamesTitle(currentReading, '') ||
  getDeepConnectionNamesTitle(currentPeople, '') ||
  getDeepConnectionNamesTitle(readingA, '') ||
  getDeepConnectionSignsTitle(currentReading, '') ||
  getDeepConnectionSignsTitle(currentPeople, '') ||
  getDeepConnectionSignsTitle(readingA, 'Este vínculo');

const titleA = isGenericReadingLabel(rawTitleA)
  ? formatConnectionLabel(fallbackTitleA, 'Este vínculo')
  : formatConnectionLabel(rawTitleA, fallbackTitleA);

const rawTitleB = String(
  readingB?.title ||
  comparedSavedSynastry?.title ||
  selectedCandidate?.title ||
  ''
).trim();

const fallbackTitleB =
  getDeepConnectionNamesTitle(comparedSavedSynastry, '') ||
  getDeepConnectionNamesTitle(selectedCandidate, '') ||
  getDeepConnectionNamesTitle(readingB, '') ||
  getDeepConnectionSignsTitle(comparedSavedSynastry, '') ||
  getDeepConnectionSignsTitle(selectedCandidate, '') ||
  getDeepConnectionSignsTitle(readingB, 'Vínculo comparado');

const titleB = isGenericReadingLabel(rawTitleB)
  ? formatConnectionLabel(fallbackTitleB, 'Vínculo comparado')
  : formatConnectionLabel(rawTitleB, fallbackTitleB);

const signTitleA =
  getDeepConnectionSignsTitle(currentReading, '') ||
  getDeepConnectionSignsTitle(currentPeople, '') ||
  getDeepConnectionSignsTitle(readingA, '') ||
  titleA;

const signTitleB =
  getDeepConnectionSignsTitle(comparedSavedSynastry, '') ||
  getDeepConnectionSignsTitle(selectedCandidate, '') ||
  getDeepConnectionSignsTitle(readingB, '') ||
  titleB;
  const dimensions = [
    { key: 'attraction', label: 'Atracción' },
    { key: 'emotional', label: 'Emocional' },
    { key: 'communication', label: 'Comunicación' },
    { key: 'stability', label: 'Estabilidad' },
    { key: 'karmic', label: 'Kármico' },
  ];

  const scoresA = getReadingScores(readingA);
  const scoresB = getReadingScores(readingB);
  const radarData = buildRadarData(scoresA, scoresB);
  const radarInsight = buildRadarInsight(scoresA, scoresB, titleA, titleB);

  const radarAxes = buildRadarAxisMeta(radarData);
  const radarCurrentPoints = buildRadarPolygonPoints(radarData, 'current');
  const radarComparedPoints = buildRadarPolygonPoints(radarData, 'compared');

 



  



 

 const verdictLove = replaceGenericLabels(
  normalizeVerdictWinner(backendVerdict?.bestForLove, titleA, titleB),
  titleA,
  titleB
);
const verdictPassion = replaceGenericLabels(
  normalizeVerdictWinner(backendVerdict?.bestForPassion, titleA, titleB),
  titleA,
  titleB
);
const verdictGrowth = replaceGenericLabels(
  normalizeVerdictWinner(backendVerdict?.bestForGrowth, titleA, titleB),
  titleA,
  titleB
);
const verdictHarmony = replaceGenericLabels(
  normalizeVerdictWinner(backendVerdict?.bestForHarmony, titleA, titleB),
  titleA,
  titleB
);
const verdictClosing = replaceGenericLabels(
  String(backendVerdict?.closing || '').trim(),
  titleA,
  titleB
);
  const narrativeClosing = safeArray(narrative?.bullets).length
    ? formatNarrativeText(
        formatConnectionLabel(
          replaceGenericLabels(
            String(
              safeArray(narrative?.bullets)[safeArray(narrative?.bullets).length - 1] || ''
            ).trim(),
            titleA,
            titleB
          ),
          ''
        )
      )
    : '';
  const executiveSummary = buildExecutiveSummary({
    titleA,
    titleB,
    highlights,
    verdictClosing,
    narrativeClosing,
    radarInsight,
  });
  const narrativeBulletItems = buildNarrativeBulletItems({
    bullets: narrative?.bullets,
    titleA,
    titleB,
    verdictClosing,
  });

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 14, marginBottom: 16 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ ...headingKickerStyle, marginBottom: 4 }}>✦ {synText('Comparativa Cósmica', 'Cosmic Comparison')}</div>
            <h1
              style={{
                margin: 0,
                fontSize: 26,
                lineHeight: 1.1,
                color: '#F7D26A',
                fontWeight: 900,
                textShadow: '0 1px 14px rgba(218,165,32,0.18)',
              }}
            >
              {synText('Descubrí qué conexión vibra más alto', 'Discover which connection resonates more strongly')}
            </h1>
            <div style={{ ...mutedStyle, marginTop: 8, maxWidth: 680 }}>
              {synText('Compará tu vínculo actual con otra sinastría guardada y descubrí cuál conexión destaca por atracción, estabilidad, resonancia emocional, fluidez mental o intensidad kármica.', 'Compare your current connection with another saved synastry reading and discover which one stands out in attraction, stability, emotional resonance, communication, or karmic intensity.')}
            </div>
          </div>

                    <button
            type="button"
            onClick={handleBack}
            style={secondaryButtonStyle}
          >
            {synText('Volver', 'Back')}
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14, marginBottom: 16 }}>
          <div style={panelStyle}>
            <div style={headingKickerStyle}>{synText('Vínculo base', 'Base connection')}</div>
            <div style={panelTitleStyle}>{currentTitle}</div>
            <div style={{ ...mutedStyle, marginTop: 8 }}>
              {synText('Compatibilidad global', 'Overall compatibility')}: <strong style={{ color: '#FFF4CC' }}>{currentMaster}</strong>
            </div>
          </div>

          <div style={panelStyle}>
            <div style={headingKickerStyle}>{synText('Elegí la conexión a comparar', 'Choose a connection to compare')}</div>
            <label
              htmlFor="synastry-comparison-select"
              style={{ display: 'block', fontSize: 12, fontWeight: 800, color: '#FFF4CC', marginBottom: 6 }}
            >
              {synText('Sinastría guardada', 'Saved synastry')}
            </label>
            <select
              id="synastry-comparison-select"
              name="synastryComparisonSelect"
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              style={{
                width: '100%',
                marginTop: 2,
                padding: '11px 12px',
                borderRadius: 12,
                border: '1px solid rgba(255,255,255,0.16)',
                background: 'rgba(18,0,40,0.96)',
                color: '#fff',
                fontSize: 14,
                outline: 'none',
              }}
            >
              <option value="">{synText('Seleccioná una sinastría guardada', 'Select a saved synastry reading')}</option>
              {savedCandidates.map((item) => (
                <option key={item.id} value={item.id}>
                  {formatConnectionLabel(
  item.title,
  getDeepConnectionNamesTitle(item, '') || getDeepConnectionSignsTitle(item, 'Lectura guardada')
)} · {item.compatibility}
                </option>
              ))}
            </select>

            <div style={{ ...mutedStyle, marginTop: 10 }}>
                            {selectedCandidate
                ? `Elegiste: ${formatConnectionLabel(
    selectedCandidate.title,
    getDeepConnectionNamesTitle(selectedCandidate, '') ||
      getDeepConnectionSignsTitle(selectedCandidate, 'Lectura comparada')
  )}`
                : savedCandidates.length
                ? synText('Seleccioná una lectura guardada para activar la comparativa.', 'Select a saved reading to activate the comparison.')
                : routeMode === 'quick'
                  ? synText('Necesitás al menos otra lectura quick guardada para comparar esta compatibilidad solar.', 'You need at least one other saved quick reading to compare this solar compatibility.')
                  : routeMode === 'deep'
                    ? synText('No hay lecturas deep guardadas disponibles para comparar.', 'No saved deep readings are available to compare.')
                    : synText('No hay lecturas guardadas disponibles para comparar.', 'No saved readings are available to compare.')}
            </div>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
              <button
                type="button"
                onClick={handleCompare}
                disabled={loadingCompare || !selectedId || !currentReading}
                style={{
                  ...primaryButtonStyle,
                  ...((loadingCompare || !selectedId || !currentReading) ? disabledButtonStyle : null),
                }}
              >
                {loadingCompare ? synText('Comparando…', 'Comparing...') : synText('Comparar conexiones', 'Compare connections')}
              </button>

                            <button
                type="button"
                onClick={() => navigate('/saved-synastries', { state: { from: routeMode || 'library' } })}
                style={secondaryButtonStyle}
              >
                {synText('Biblioteca cósmica', 'Cosmic library')}
              </button>
            <button
              type="button"
              onClick={handleShareComparison}
              disabled={!comparison}
              style={{
                ...secondaryButtonStyle,
                ...(!comparison ? disabledButtonStyle : null),
              }}
            >
              Descargar / compartir card
            </button>
            </div>
            {shareStatus ? (
              <div style={{ ...mutedStyle, marginTop: 10, color: '#FFF4CC' }}>
                {shareStatus}
              </div>
            ) : null}
{sharePanelOpen ? (
  <div
    style={{
      marginTop: 12,
      padding: 12,
      borderRadius: 16,
      border: '1px solid rgba(255,255,255,0.12)',
      background: 'linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02)), rgba(0,0,0,0.18)',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
      gap: 10,
    }}
  >
    <button type="button" onClick={handleShareWhatsApp} style={secondaryButtonStyle}>
      WhatsApp
    </button>
    <button type="button" onClick={handleShareInstagram} style={secondaryButtonStyle}>
      Instagram
    </button>
    <button type="button" onClick={handleShareFacebook} style={secondaryButtonStyle}>
      Facebook
    </button>
    <button type="button" onClick={handleShareTelegram} style={secondaryButtonStyle}>
      Telegram
    </button>
    <button type="button" onClick={handleShareX} style={secondaryButtonStyle}>
      X / Twitter
    </button>
    <button type="button" onClick={handleCopyComparisonText} style={secondaryButtonStyle}>
      Copiar resumen
    </button>
    <button type="button" onClick={handleDownloadComparisonCard} style={secondaryButtonStyle}>
      Descargar archivo
    </button>
  </div>
) : null}

          </div>
        </div>

        {error ? (
          <div
            style={{
              ...panelStyle,
              border: '1px solid rgba(255,130,130,0.34)',
              background: 'rgba(65,0,22,0.28)',
              color: '#FFD7D7',
              marginBottom: 16,
            }}
          >
            {error}
          </div>
        ) : null}

        {!comparison ? (
          <div style={panelStyle}>
            <div style={headingKickerStyle}>{synText('Cómo funciona', 'How it works')}</div>
            <div style={panelTitleStyle}>{synText('Elegí otra sinastría guardada para revelar la comparativa', 'Choose another saved synastry reading to reveal the comparison')}</div>
                        <div style={{ ...mutedStyle, marginTop: 10 }}>
              {routeMode === 'quick'
                ? synText('Esta comparativa está en modo quick: solo vas a comparar compatibilidades solares entre lecturas quick guardadas.', 'This comparison is in quick mode: it compares solar compatibility between saved quick readings only.')
                : routeMode === 'deep'
                  ? synText('Esta comparativa está en modo deep: solo vas a comparar sinastrías profundas entre lecturas deep guardadas.', 'This comparison is in deep mode: it compares saved deep synastry readings only.')
                  : synText('Vas a ver compatibilidad global, arquitectura energética, aspectos dominantes y una lectura premium que revele la identidad relacional de cada vínculo.', 'You will see overall compatibility, energetic structure, dominant aspects, and a premium reading of each connection.')}
            </div>
          </div>
        ) : null}

        {comparison ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14 }}>
              <div style={panelStyle}>
                <div style={headingKickerStyle}>{synText('Vínculo actual', 'Current connection')}</div>
                <div style={panelTitleStyle}>{titleA}</div>
                <div style={{ fontSize: 34, fontWeight: 900, color: '#F7D26A', marginTop: 10 }}>
                  {getCompatibilityValue(readingA)}
                </div>
              </div>

              <div style={panelStyle}>
                <div style={headingKickerStyle}>{synText('Vínculo comparado', 'Compared connection')}</div>
                <div style={panelTitleStyle}>{titleB}</div>
                <div style={{ fontSize: 34, fontWeight: 900, color: '#F7D26A', marginTop: 10 }}>
                  {getCompatibilityValue(readingB)}
                </div>
              </div>

              <div style={panelStyle}>
                <div style={headingKickerStyle}>{synText('Diferencia central', 'Core difference')}</div>
                <div style={panelTitleStyle}>{synText('Diferencia de compatibilidad', 'Compatibility difference')}</div>
                <div style={{ fontSize: 34, fontWeight: 900, color: getDeltaTone(delta?.compatibility), marginTop: 10 }}>
                  {formatSignedDelta(delta?.compatibility)}
                </div>
              </div>
            </div>

            <div style={panelStyle}>
              <div style={headingKickerStyle}>✦ {synText('ADN energético del vínculo', 'Energetic DNA of the connection')}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.2fr) minmax(260px, 0.8fr)', gap: 14, alignItems: 'stretch' }}>
                <div style={radarShellStyle}>
                  <div style={{ width: '100%', maxWidth: 420, margin: '0 auto' }}>
                    <svg viewBox="0 0 360 360" width="100%" height="360" role="img" aria-label={synText('Mapa de resonancia del vínculo', 'Connection resonance map')}>
                      {[30, 60, 90, 120].map((ring) => (
                        <polygon
                          key={`ring-${ring}`}
                          points={buildRadarRingPoints(radarData.length, ring)}
                          fill="none"
                          stroke="rgba(255,255,255,0.12)"
                          strokeWidth="1"
                        />
                      ))}

                      {radarAxes.map((axis) => (
                        <g key={axis.key}>
                          <line
                            x1="180"
                            y1="180"
                            x2={axis.end.x}
                            y2={axis.end.y}
                            stroke="rgba(255,255,255,0.14)"
                            strokeWidth="1"
                          />
                          <text
                            x={axis.label.x}
                            y={axis.label.y}
                            fill="#FFF4CC"
                            fontSize="12"
                            fontWeight="800"
                            textAnchor="middle"
                            dominantBaseline="middle"
                          >
                            {axis.dimension}
                          </text>
                        </g>
                      ))}

                      <polygon
                        points={radarComparedPoints}
                        fill="#7C3AED"
                        fillOpacity="0.18"
                        stroke="#C084FC"
                        strokeWidth="2.5"
                      />
                      <polygon
                        points={radarCurrentPoints}
                        fill="#DAA520"
                        fillOpacity="0.28"
                        stroke="#DAA520"
                        strokeWidth="2.5"
                      />

                      <circle cx="180" cy="180" r="3.5" fill="#FFF4CC" opacity="0.9" />
                    </svg>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={statCardStyle}>
                    <div style={microLabelStyle}>{synText('Mapa de resonancia', 'Resonance map')}</div>
                    <div style={{ ...panelTitleStyle, marginTop: 6 }}>{synText('Mapa relacional comparado', 'Compared relationship map')}</div>
                    <div style={{ ...mutedStyle, marginTop: 8 }}>
  {synText('Superpone ambas conexiones para revelar de forma inmediata cuál sostiene mejor la estructura energética del vínculo.', 'Overlays both connections to show immediately which one sustains the stronger energetic structure.')}
</div>
                  </div>

                  <div style={statCardStyle}>
                    <div style={microLabelStyle}>{synText('Lectura visual', 'Visual reading')}</div>
                    <div style={{ ...insightTextStyle, marginTop: 8 }}>{radarInsight}</div>
                  </div>

                  <div style={statCardStyle}>
                    <div style={microLabelStyle}>{synText('Leyenda energética', 'Energetic legend')}</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                        <span style={radarLegendDotStyle('#DAA520')} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 900, color: '#FFF4CC' }}>{titleA}</div>
                          <div style={{ ...mutedStyle, marginTop: 2 }}>{synText('Representa el vínculo actual y su huella energética completa.', 'Represents the current connection and its complete energetic signature.')}</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                        <span style={radarLegendDotStyle('#C084FC')} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 900, color: '#FFF4CC' }}>{titleB}</div>
                          <div style={{ ...mutedStyle, marginTop: 2 }}>{synText('Representa la lectura comparada y su estructura relacional dominante.', 'Represents the compared reading and its dominant relationship structure.')}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div style={panelStyle}>
              <div style={headingKickerStyle}>{synText('Arquitectura energética del vínculo', 'Energetic structure of the connection')}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {dimensions.map((item) => {
                  const valueA = getDimensionValue(readingA, item.key);
                  const valueB = getDimensionValue(readingB, item.key);
                  const diff = n(delta?.[item.key]);
                  const winner = valueA > valueB ? titleA : valueB > valueA ? titleB : synText('Empate', 'Tie');
                  return (
                    <div
                      key={item.key}
                      style={{
                        background: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: 14,
                        padding: '12px 12px 13px',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', marginBottom: 10 }}>
                        <div style={{ fontWeight: 900, color: '#FFF4CC', fontSize: 13 }}>{item.label}</div>
                        <div style={{ fontSize: 11, fontWeight: 900, color: '#F7D26A' }}>
                          {synText('Domina', 'Leads')}: {winner}
                        </div>
                      </div>

                      <div style={{ display: 'grid', gap: 8 }}>
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 5 }}>
                            <div style={{ fontSize: 12, fontWeight: 800 }}>{titleA}</div>
                            <div style={{ fontSize: 12, fontWeight: 900 }}>{valueA}</div>
                          </div>
                          <div style={{ height: 8, borderRadius: 999, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
                            <div style={buildBar(valueA)} />
                          </div>
                        </div>

                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 5 }}>
                            <div style={{ fontSize: 12, fontWeight: 800 }}>{titleB}</div>
                            <div style={{ fontSize: 12, fontWeight: 900 }}>{valueB}</div>
                          </div>
                          <div style={{ height: 8, borderRadius: 999, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
                            <div style={buildBar(valueB)} />
                          </div>
                        </div>
                      </div>

                      <div style={{ marginTop: 10, fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>
                        {synText('Diferencia', 'Difference')}: <strong style={{ color: getDeltaTone(diff) }}>{formatSignedDelta(diff)}</strong>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
              <div style={panelStyle}>
                <div style={headingKickerStyle}>{synText('Aspectos dominantes · Vínculo actual', 'Dominant aspects · Current connection')}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {safeArray(readingA?.aspects).length ? (
                    safeArray(readingA?.aspects).map((item, index) => (
                      <div
                        key={`aspect-a-${index}`}
                        style={{
                          background: 'rgba(255,255,255,0.04)',
                          border: '1px solid rgba(255,255,255,0.08)',
                          borderRadius: 12,
                          padding: '9px 10px',
                          fontSize: 13,
                          lineHeight: 1.35,
                        }}
                      >
                        {aspectLine(item)}
                      </div>
                    ))
                  ) : (
                    <div style={mutedStyle}>{synText('No hay aspectos destacados disponibles.', 'No highlighted aspects are available.')}</div>
                  )}
                </div>
              </div>

              <div style={panelStyle}>
                <div style={headingKickerStyle}>{synText('Aspectos dominantes · Vínculo comparado', 'Dominant aspects · Compared connection')}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {safeArray(readingB?.aspects).length ? (
                    safeArray(readingB?.aspects).map((item, index) => (
                      <div
                        key={`aspect-b-${index}`}
                        style={{
                          background: 'rgba(255,255,255,0.04)',
                          border: '1px solid rgba(255,255,255,0.08)',
                          borderRadius: 12,
                          padding: '9px 10px',
                          fontSize: 13,
                          lineHeight: 1.35,
                        }}
                      >
                        {aspectLine(item)}
                      </div>
                    ))
                  ) : (
                    <div style={mutedStyle}>{synText('No hay aspectos destacados disponibles.', 'No highlighted aspects are available.')}</div>
                  )}
                </div>
              </div>
            </div>

            

           

           

           

            <div style={panelStyle}>
              <div style={headingKickerStyle}>{synText('Highlights automáticos', 'Automatic highlights')}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                <div style={statCardStyle}>
                  <div style={microLabelStyle}>{synText('Vínculo más fuerte', 'Strongest connection')}</div>
                  <div style={{ ...panelTitleStyle, marginTop: 6 }}>{replaceGenericLabels(getWinnerLabel(highlights?.strongestBond, titleA, titleB), titleA, titleB)}</div>
                  <div style={{ ...mutedStyle, marginTop: 8 }}>{synText('Marca qué conexión sobresale con más fuerza en la lectura general.', 'Shows which connection stands out most strongly in the overall reading.')}</div>
                </div>
                <div style={statCardStyle}>
                  <div style={microLabelStyle}>{synText('Más atracción', 'More attraction')}</div>
                  <div style={{ ...panelTitleStyle, marginTop: 6 }}>{replaceGenericLabels(getWinnerLabel(highlights?.mostPassionateBond, titleA, titleB), titleA, titleB)}</div>
                  <div style={{ ...mutedStyle, marginTop: 8 }}>{synText('Concentra más química, magnetismo visible y energía de deseo entre ambas personas.', 'Concentrates more chemistry, visible magnetism, and desire between both people.')}</div>
                </div>
                <div style={statCardStyle}>
                  <div style={microLabelStyle}>{synText('Más estabilidad', 'More stability')}</div>
                  <div style={{ ...panelTitleStyle, marginTop: 6 }}>{replaceGenericLabels(getWinnerLabel(highlights?.mostStableBond, titleA, titleB), titleA, titleB)}</div>
                  <div style={{ ...mutedStyle, marginTop: 8 }}>{synText('Proyecta mayor sostén, consistencia y capacidad para mantenerse en el tiempo.', 'Shows greater support, consistency, and long-term potential.')}</div>
                </div>
                <div style={statCardStyle}>
                  <div style={microLabelStyle}>{synText('Más resonancia emocional', 'More emotional resonance')}</div>
                  <div style={{ ...panelTitleStyle, marginTop: 6 }}>{replaceGenericLabels(getWinnerLabel(highlights?.mostEmotionalBond, titleA, titleB), titleA, titleB)}</div>
                  <div style={{ ...mutedStyle, marginTop: 8 }}>{synText('Activa una sensibilidad más profunda y una respuesta emocional más intensa.', 'Activates deeper sensitivity and a more intense emotional response.')}</div>
                </div>
                <div style={statCardStyle}>
                  <div style={microLabelStyle}>{synText('Más intensidad kármica', 'More karmic intensity')}</div>
                  <div style={{ ...panelTitleStyle, marginTop: 6 }}>{replaceGenericLabels(getWinnerLabel(highlights?.mostKarmicBond, titleA, titleB), titleA, titleB)}</div>
                  <div style={{ ...mutedStyle, marginTop: 8 }}>{synText('Sugiere más aprendizaje, huella transformadora y sensación de vínculo significativo.', 'Suggests deeper lessons, transformative impact, and a stronger sense of significance.')}</div>
                </div>
              </div>
            </div>
            <div style={panelStyle}>
              <div style={headingKickerStyle}>{synText('Veredicto relacional', 'Relationship verdict')}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                <div style={statCardStyle}>
                  <div style={microLabelStyle}>{synText('Mejor para amor y sostén', 'Best for love and support')}</div>
                  <div style={{ ...panelTitleStyle, marginTop: 6 }}>
                    {verdictLove === 'Conexión A' ? titleA : verdictLove === 'Conexión B' ? titleB : verdictLove}
                  </div>
                  <div style={{ ...mutedStyle, marginTop: 8 }}>{synText('Integra mejor sostén emocional, estabilidad práctica y capacidad de construir algo duradero.', 'Better combines emotional support, practical stability, and the ability to build something lasting.')}</div>
                </div>
                <div style={statCardStyle}>
                  <div style={microLabelStyle}>{synText('Mejor para pasión', 'Best for passion')}</div>
                  <div style={{ ...panelTitleStyle, marginTop: 6 }}>
                    {verdictPassion === 'Conexión A' ? titleA : verdictPassion === 'Conexión B' ? titleB : verdictPassion}
                  </div>
                  <div style={{ ...mutedStyle, marginTop: 8 }}>{synText('Canaliza con más fuerza la química, el deseo y el magnetismo relacional inmediato.', 'Channels chemistry, desire, and immediate relationship magnetism more strongly.')}</div>
                </div>
                <div style={statCardStyle}>
                  <div style={microLabelStyle}>{synText('Mejor para crecimiento', 'Best for growth')}</div>
                  <div style={{ ...panelTitleStyle, marginTop: 6 }}>
                    {verdictGrowth === 'Conexión A' ? titleA : verdictGrowth === 'Conexión B' ? titleB : verdictGrowth}
                  </div>
                  <div style={{ ...mutedStyle, marginTop: 8 }}>{synText('Indica qué vínculo tiene mayor potencia transformadora y capacidad de dejar huella evolutiva.', 'Shows which connection has greater transformative power and growth potential.')}</div>
                </div>
                <div style={statCardStyle}>
                  <div style={microLabelStyle}>{synText('Mejor para armonía global', 'Best overall harmony')}</div>
                  <div style={{ ...panelTitleStyle, marginTop: 6 }}>
                    {verdictHarmony === 'Conexión A' ? titleA : verdictHarmony === 'Conexión B' ? titleB : verdictHarmony}
                  </div>
                  <div style={{ ...mutedStyle, marginTop: 8 }}>{synText('Resume qué conexión reúne la arquitectura más completa y armónica dentro del conjunto.', 'Summarizes which connection has the most complete and harmonious overall structure.')}</div>
                </div>
              </div>
              
            </div>

            <div style={panelStyle}>
  <div style={headingKickerStyle}>{synText('Cierre narrativo', 'Narrative conclusion')}</div>
  <div style={panelTitleStyle}>{synText('Lectura final de la comparativa', 'Final comparison reading')}</div>
  <div style={{ ...mutedStyle, marginTop: 10 }}>
    {synText('Una síntesis breve para condensar qué vínculo domina la comparativa y desde qué tipo de energía lo hace.', 'A brief synthesis of which connection leads the comparison and the energy behind it.')}
  </div>

  <div
    style={{
      marginTop: 12,
      background: 'linear-gradient(180deg, rgba(247,210,106,0.08), rgba(255,255,255,0.03))',
      border: '1px solid rgba(247,210,106,0.16)',
      borderRadius: 14,
      padding: '14px 15px',
    }}
  >
    <div style={microLabelStyle}>{synText('Síntesis final', 'Final synthesis')}</div>
    <div style={{ ...insightTextStyle, marginTop: 8 }}>{executiveSummary}</div>
  </div>

  {narrativeBulletItems.length ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
      {narrativeBulletItems.map((bullet, index) => (
        <div key={`bullet-${index}`} style={bulletCardStyle}>
          <div style={{ color: '#F7D26A', fontWeight: 900 }}>✦</div>
          <div style={{ fontSize: 13, lineHeight: 1.45 }}>{bullet}</div>
        </div>
      ))}
    </div>
  ) : null}
</div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
